import React from 'react'
import LazyLoad from 'react-lazy-load'

import SelectCampaign from './SelectCampaign'
import styles from './GroupAddCreate/groupAddCreate.module.scss'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import { Button, Dialog, Text } from '@sendoutcards/quantum-design-ui'
import {
  GiftVariation,
  MinimalCampaignFragment,
} from 'src/graphql/generated/graphql'
import { useActions, useState } from 'src/hooks'
import { FlexRow } from 'src/styled'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeyStore } from 'src/react_query'

type SelectCampaignModalProps = {
  isOpen: boolean
  recipientsCount: number
  onClose: () => void
  onSubmit: (campaign: MinimalCampaignFragment) => void
}

const UnavailableGift: React.FC<{
  giftVariation: GiftVariation
}> = props => {
  const { giftVariation } = props
  const title = giftVariation.name
  const titleLength = 35
  const displayTitle =
    title.length > titleLength
      ? title.substring(0, titleLength - 3) + '...'
      : title

  return (
    <div
      id={giftVariation.id}
      style={{
        display: 'flex',
      }}
    >
      <>
        <div
          style={{
            backgroundColor: '#eeefef',
            width: '62px',
            height: '62px',
          }}
        >
          <LazyLoad height={62} offset={62}>
            <div
              style={{
                backgroundImage: `url(${giftVariation.defaultImageUrl})`,
                width: '100%',
                height: '100%',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
              }}
            />
          </LazyLoad>
        </div>
        <div>
          <Text type="largeBody" inset="x1">
            {displayTitle}
          </Text>
        </div>
      </>
    </div>
  )
}

const UnavailableGifts: React.FC<{
  campaignId: string
  unavailableGifts: GiftVariation[]
  onBack: () => void
}> = props => {
  const actions = useActions()
  const { unavailableGifts } = props
  const length = unavailableGifts.length
  const plural = length > 1 ? 's' : ''
  const handleOnBack = () => {
    props.onBack()
  }
  const handleOnUpdateCampaign = () => {
    actions.openCampaign(props.campaignId)
  }
  return (
    <>
      <Text type="body" inset="x0" outset={{ bottom: 'x1' }}>
        {`The selected campaign has ${length} unavailable gift${plural}:`}
      </Text>
      {props.unavailableGifts.map(value => (
        <UnavailableGift giftVariation={value} />
      ))}
      <FlexRow style={{ justifyContent: 'space-around', marginTop: '1rem' }}>
        <div>
          <Button
            id={'select_btn'}
            title="Back"
            backgroundColorOverride="#e841b3"
            onClick={handleOnBack}
            fontWeight="extraBold"
            size="small"
          />
        </div>
        <div>
          <Button
            id={'update_btn'}
            title="Update Campaign"
            backgroundColorOverride="#e841b3"
            onClick={handleOnUpdateCampaign}
            fontWeight="extraBold"
            size="small"
          />
        </div>
      </FlexRow>
    </>
  )
}

const SelectCampaignModal: React.FC<SelectCampaignModalProps> = props => {
  const { onSubmit, onClose, isOpen, recipientsCount } = props
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedCampaign, setSelectedCampaign] =
    useState<MinimalCampaignFragment>()
  const [unavailableGifts, setUnavailableGifts] = useState<GiftVariation[]>()
  const queryClient = useQueryClient()
  const title = unavailableGifts
    ? `${selectedCampaign?.name}`
    : 'Select Campaign to Send'

  const handleOnSubmit = async (campaign: MinimalCampaignFragment) => {
    setIsSubmitting(true)
    const unavailableGifts = await queryClient.fetchQuery({
      ...queryKeyStore.campaign.unavailableGifts({
        id: campaign.id,
        numRecipients: recipientsCount,
      }),
    })
    if (unavailableGifts.length > 0) {
      setSelectedCampaign(campaign)
      setUnavailableGifts(unavailableGifts)
      setIsSubmitting(false)
      return
    }

    onSubmit(campaign)
  }

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <DialogTitle>
        <Text type="largeBody">{title}</Text>
      </DialogTitle>
      <DialogContent className={styles.modalContent}>
        {selectedCampaign && unavailableGifts && unavailableGifts.length > 0 ? (
          <UnavailableGifts
            campaignId={selectedCampaign?.id}
            unavailableGifts={unavailableGifts}
            onBack={() => {
              setSelectedCampaign(undefined)
              setUnavailableGifts(undefined)
            }}
          />
        ) : (
          <SelectCampaign
            isSubmitting={isSubmitting}
            onClose={onClose}
            onSubmit={handleOnSubmit}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

export default SelectCampaignModal
